import React, { useMemo, Fragment } from 'react';
import { ScissorsIcon } from 'lucide-react';
import cvData from '../../data/cv';
import { Masthead } from './Masthead';
import { Rubric } from './Rubric';
import { ExperienceEntry } from './ExperienceEntry';
import { ProfileBlock, CapabilitiesBlock, EducationBlock } from './Aside';
import { EarlierExperience, type CondensedRole } from './EarlierExperience';
import { Page } from './Page';
import { periodSortKey, condenseRole } from './helpers';
import { useMeasuredPages, type Block } from './useMeasuredPages';
// A4 content area height: 297mm − 28mm margins ≈ 1017px @96dpi, minus the
// page footer (~46px) so packed content never overflows into it.
const PAGE_HEIGHT_PX = 928;
const CONTENT_WIDTH_PX = 680;
export function CVSheet({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    experience,
    earlierExperience,
    meta
  } = cvData;
  const S = meta.sections;
  // Condensed mode: a fuller detailed set (spilling Earlier Experience onto a
  // third page) reads better than cramming everything into two pages.
  const detailedCount = compact ? 5 : meta.detailedRoleCount;
  const roles = useMemo(() => [...experience].sort((a, b) => periodSortKey(b.period) - periodSortKey(a.period)), [experience]);
  const detailed = useMemo(() => roles.slice(0, detailedCount), [roles, detailedCount]);
  const condensed: CondensedRole[] = useMemo(() => [...roles.slice(detailedCount).map(condenseRole), ...earlierExperience], [roles, detailedCount, earlierExperience]);
  const blocks: Block[] = useMemo(() => {
    const list: Block[] = [];
    const gap = compact ? 'mt-4' : 'mt-11';
    // Masthead + a balanced two-column intro band: Profile (left) +
    // Capabilities (right), one cohesive header block.
    list.push({
      key: 'intro',
      node: <>
          <Masthead compact={compact} data-id="element-59" data-mp="24wcph79eco5c" />
          <div className={`${compact ? 'mt-4 gap-x-8 gap-y-4' : 'mt-10 gap-x-12 gap-y-10'} grid grid-cols-12`} data-id="element-60" data-mp="29suwkds78yku">
            <div className={compact ? 'col-span-5' : 'col-span-7'} data-id="element-61" data-mp="21t6x70ep87jv">
              <ProfileBlock compact={compact} data-id="element-62" data-mp="1wudmtgupkwnx" />
            </div>
            <div className={compact ? 'col-span-7' : 'col-span-5'} data-id="element-63" data-mp="2bquu9wzs1r57">
              <CapabilitiesBlock compact={compact} data-id="element-64" data-mp="2bs5xnvpb8wvs" />
            </div>
          </div>
        </>
    });
    list.push({
      key: 'exp-head',
      keepWithNext: true,
      node: <div className={gap} data-id="element-65" data-mp="2kpydr22uw8k8">
          <Rubric index={S.experience.index} title={S.experience.title} count={`${detailed.length} recent roles`} compact={compact} data-id="element-66" data-mp="2kp6k3wstkkwl" />
        </div>
    });
    detailed.forEach((item, i) => {
      list.push({
        key: `role-${i}`,
        node: <ExperienceEntry item={item} isLast compact={compact} data-id="element-67" data-mp="33i0nrukzsqli" />
      });
    });
    if (!compact) {
      // Full mode: each earlier role as its own condensed row.
      list.push({
        key: 'earlier-head',
        keepWithNext: true,
        node: <div className={gap} data-id="element-68" data-mp="3i594tc0zfq5e">
            <Rubric index={S.earlier.index} title={S.earlier.title} count={`${condensed.length} roles`} data-id="element-69" data-mp="3i2wr5b29c1y5" />
          </div>
      });
      condensed.forEach((r, i) => {
        list.push({
          key: `cond-${i}`,
          node: <EarlierExperience roles={[r]} data-id="element-70" data-mp="3e58rjupmdkzx" />
        });
      });
    } else if (condensed.length > 0) {
      // Condensed mode: a tight "Earlier Experience" section (2-column rows to
      // halve its height) with Education, kept as one block that flows right
      // after the detailed roles — no forced page break, so it fits in 3 pages.
      list.push({
        key: 'earlier',
        keepWithNext: true,
        node: <div className={gap} data-id="element-71" data-mp="2gfnj18507be0">
            <Rubric index={S.earlier.index} title={S.earlier.title} count={`${condensed.length} roles`} compact={compact} data-id="element-72" data-mp="2ggf5ntgmhys5" />
            <ul className="grid grid-cols-2 gap-x-8 gap-y-0.5" data-id="element-73" data-mp="3ns4tauqluloe">
              {condensed.map((r, i) => <li key={i} className="grid grid-cols-[78px_1fr] items-baseline gap-2 border-b border-[var(--line)] py-1" data-id="element-74" data-mp="3jrchbtqcm2of" data-mpi={i}>
                  <span className="whitespace-nowrap font-mono text-[8.5px] tabular-nums text-[var(--ink-faint)]" data-id="element-75" data-mp="3a100abhqsrn1">
                    {r.period}
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0" data-id="element-76" data-mp="34z0w4ze6hqp1">
                    <span className="font-display text-[11px] font-medium leading-snug text-[var(--ink)]" data-id="element-77" data-mp="304es5haff0ez">
                      {r.company}
                    </span>
                    <span className="font-mono text-[8px] uppercase tracking-[0.06em] text-[var(--accent)]" data-id="element-78" data-mp="2r2xes6oelctz">
                      {r.role}
                    </span>
                  </div>
                </li>)}
            </ul>
          </div>
      });
      list.push({
        key: 'edu',
        node: <div className={gap} data-id="element-79" data-mp="wrnujwq863rs">
            <EducationBlock compact={compact} data-id="element-80" data-mp="11sl9m0fxuhp2" />
          </div>
      });
      return list;
    }
    list.push({
      key: 'edu-head',
      keepWithNext: true,
      node: <div className={gap} data-id="element-81" data-mp="16lxohiutfa4m" />
    });
    list.push({
      key: 'edu',
      node: <EducationBlock compact={compact} data-id="element-82" data-mp="1bhv57q9zqihx" />
    });
    return list;
  }, [detailed, condensed, S, compact]);
  const {
    setRef,
    pages,
    heights: dbg
  } = useMeasuredPages(blocks, PAGE_HEIGHT_PX, String(compact));
  // Hidden measuring layer. offsetHeight is transform-independent, so measuring
  // works even though CVSheet renders inside the zoom/pan transform. Rendered as
  // a plain absolutely-positioned sibling (no portal needed).
  const measuringLayer = <div aria-hidden="true" className="no-print" style={{
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    left: '-99999px',
    top: 0,
    width: `${CONTENT_WIDTH_PX}px`
  }} data-id="element-83" data-mp="1z77culpo6ii4">
      {blocks.map(b => <div key={b.key} ref={setRef(b.key)} className="mb-5" data-id="element-84" data-mp="3vc6qrk3p8yyc">
          {b.node}
        </div>)}
    </div>;
  const total = pages.length;
  return <div className="relative flex flex-col items-center" data-id="element-85" data-mp="d3ei5denxesx">
      {measuringLayer}
      {/* TEMP DEBUG */}
      <pre className="no-print mb-4 rounded bg-black/85 p-3 text-[10px] leading-tight text-lime-300" data-id="element-86" data-mp="3gmh98ebauibw">
        {`PAGE_HEIGHT=${PAGE_HEIGHT_PX}  pages=${total}\n` + pages.map((pg, i) => `— page ${i + 1}: ` + pg.map(b => `${b.key}(${dbg[b.key] ?? '…'})`).join(' ')).join('\n')}
      </pre>
      {pages.map((pageBlocks, pageIdx) => <Fragment key={pageIdx} data-id="element-87" data-mp="2xu6o9zglp522" data-mpi={pageIdx}>
          {/* On-screen page-break divider (never printed). */}
          {pageIdx > 0 && <div className="no-print flex w-[210mm] max-w-full items-center gap-3 py-5" aria-hidden="true" data-id="element-88" data-mp="18evz1vy9ac99">
              <span className="h-px flex-1 bg-[var(--line-strong)]" data-id="element-89" data-mp="1d6dxodct40r2" />
              <span className="flex items-center gap-1.5 rounded-full border border-[var(--line-strong)] bg-white/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--ink-faint)]" data-id="element-90" data-mp="zeplka2k5ca3">
                <ScissorsIcon className="h-3 w-3" data-id="element-91" data-mp="1s2lgpachaw4r" />
                Page {pageIdx + 1} of {total}
              </span>
              <span className="h-px flex-1 bg-[var(--line-strong)]" data-id="element-92" data-mp="1w35e8f0ys1rf" />
            </div>}
          <Page index={pageIdx + 1} total={total} data-id="element-93" data-mp="1i3vxne8uj5mx">
            <div className={compact ? 'space-y-3' : 'space-y-5'} data-id="element-94" data-mp="1n3s2tgta5342">
              {pageBlocks.map(b => <div key={b.key} data-id="element-95" data-mp="6ps60epqdtl1">{b.node}</div>)}
            </div>
          </Page>
        </Fragment>)}
    </div>;
}